<template>
  <v-dialog
    v-model="dialog"
    max-width="1180px"
    scrollable
  >
    <v-card>
      <v-card-title class="d-block">
        <div class="text-h5">
          Update Version
        </div>
        <div class="device-version-update-subtitle mt-1">
          Choose an app and APK version for each selected device, then queue the silent update.
        </div>
      </v-card-title>

      <v-card-text>
        <command-target-selector
          :mode="target.mode"
          :group-id="target.groupId"
          :groups="deviceGroups"
          :device-count="devices.length"
          :loading="loadingDeviceGroups"
          :error="target.error"
          @update:mode="target.mode = $event; target.error = ''"
          @update:groupId="target.groupId = $event; target.error = ''"
        />
        <v-divider class="mb-4" />

        <v-alert
          dense
          text
          type="info"
          class="mb-4"
        >
          <span v-if="target.mode === 'group'">
            One app and APK version is queued for every device in the group.
          </span>
          <span v-else>
            Devices that pick the same APK are sent together through the bulk update API.
          </span>
        </v-alert>

        <div class="device-version-update-toolbar mb-4">
          <div v-if="target.mode === 'devices'" class="subtitle-2">
            {{ devices.length }} selected {{ devices.length === 1 ? 'device' : 'devices' }}
          </div>
          <div v-else class="subtitle-2">
            Group target
          </div>
          <v-btn
            small
            text
            :loading="catalogLoading"
            @click="loadCatalog({ force: true })"
          >
            <v-icon
              small
              class="mr-1"
            >
              mdi-refresh
            </v-icon>
            Refresh APKs
          </v-btn>
        </div>

        <v-card
          outlined
          class="device-version-update-defaults mb-4"
        >
          <v-card-text class="pb-2">
            <div class="subtitle-2 mb-3">
              {{ target.mode === 'group' ? 'Group selection' : 'Default selection' }}
            </div>

            <v-row dense align="center">
              <v-col
                cols="12"
                md="4"
              >
                <v-autocomplete
                  v-model="defaultPackageName"
                  :items="appChoices"
                  item-text="displayLabel"
                  item-value="packageName"
                  label="Default App"
                  dense
                  outlined
                  hide-details
                  :disabled="submitting || !appChoices.length"
                  @change="onDefaultPackageChanged"
                />
              </v-col>

              <v-col
                cols="12"
                md="5"
              >
                <v-autocomplete
                  v-model="defaultSelectedApkId"
                  :items="getApkOptionsForPackage(defaultPackageName)"
                  item-text="displayLabel"
                  item-value="id"
                  label="Default APK Version"
                  dense
                  outlined
                  hide-details
                  :disabled="submitting || !defaultPackageName"
                  :no-data-text="defaultPackageName ? 'No APKs found for this app' : 'Select an app first'"
                >
                  <template v-slot:item="{ item, on, attrs }">
                    <v-list-item
                      v-bind="attrs"
                      :class="{ 'default-version-option': isDefaultApkOption(item, defaultPackageName) }"
                      v-on="on"
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ item.displayLabel }}
                          <v-chip
                            v-if="isDefaultApkOption(item, defaultPackageName)"
                            x-small
                            color="success"
                            text-color="white"
                            class="ml-2"
                          >
                            Default
                          </v-chip>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col
                v-if="target.mode === 'devices'"
                cols="12"
                md="3"
                class="device-version-update-defaults-action"
              >
                <v-btn
                  :color="isAppliedDefaultActive ? 'secondary' : 'primary'"
                  depressed
                  :disabled="submitting || !defaultPackageName || !defaultSelectedApkId || !updateRows.length"
                  @click="isAppliedDefaultActive ? clearAppliedDefault() : applyDefaultSelection()"
                >
                  {{ isAppliedDefaultActive ? 'Clear Default' : 'Apply To Devices' }}
                </v-btn>
              </v-col>
            </v-row>

            <div class="caption mt-3">
              <span v-if="target.mode === 'group'">
                This app and version is sent to every device in the selected group.
              </span>
              <span v-else>
                Pick one app and version here to prefill all listed devices, then adjust any row individually if needed.
              </span>
            </div>
          </v-card-text>
        </v-card>

        <v-alert
          v-if="catalogError"
          dense
          text
          type="warning"
          class="mb-4"
        >
          {{ catalogError }}
        </v-alert>

        <v-alert
          v-else-if="!catalogLoading && !appChoices.length"
          dense
          text
          type="error"
          class="mb-4"
        >
          No matching APK metadata was found for this company in Scripts Downloads.
        </v-alert>

        <div
          v-if="catalogLoading"
          class="device-version-update-loading py-8"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="36"
          />
        </div>

        <div v-else-if="target.mode === 'devices'">
          <v-card
            v-for="row in updateRows"
            :key="row.macAddress || row.id"
            outlined
            class="mb-3"
          >
            <v-card-text>
              <v-row dense>
                <v-col
                  cols="12"
                  md="2"
                >
                  <div class="font-weight-medium">
                    {{ row.deviceName }}
                  </div>
                  <div
                    v-if="row.companyName"
                    class="mt-1"
                  >
                    <span
                      class="device-version-update-company"
                      :style="getCompanyBadgeStyle(row.companyName)"
                    >
                      {{ row.companyName }}
                    </span>
                  </div>
                  <div
                    v-if="row.deviceIdentifier && row.deviceIdentifier !== row.deviceName"
                    class="caption mt-1"
                  >
                    {{ row.deviceIdentifier }}
                  </div>
                </v-col>

                <v-col
                  cols="12"
                  md="2"
                >
                  <div class="caption text-uppercase grey--text text--darken-1 mb-1">
                    Current
                  </div>
                  <div class="body-2">
                    {{ row.currentRequiredVersion || 'No required version' }}
                  </div>
                  <div class="caption mt-1">
                    Installed: {{ row.currentAppVersion || 'Unknown' }}
                  </div>
                </v-col>

                <v-col
                  cols="12"
                  md="7"
                >
                  <div
                    v-for="(selection, selectionIndex) in row.selections"
                    :key="selection.id"
                    :class="selectionIndex === 0 ? '' : 'mt-2'"
                  >
                    <v-row dense>
                      <v-col
                        cols="12"
                        md="5"
                      >
                        <v-autocomplete
                          v-model="selection.packageName"
                          :items="appChoices"
                          item-text="displayLabel"
                          item-value="packageName"
                          :label="selectionIndex === 0 ? 'App' : 'Additional App'"
                          dense
                          outlined
                          hide-details
                          :disabled="submitting || !appChoices.length"
                          @change="onSelectionPackageChanged(row, selection)"
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        :md="selectionIndex > 0 ? 6 : 7"
                      >
                        <v-autocomplete
                          v-model="selection.selectedApkId"
                          :items="getApkOptionsForPackage(selection.packageName)"
                          item-text="displayLabel"
                          item-value="id"
                          :label="selectionIndex === 0 ? 'APK Version' : 'Additional APK Version'"
                          dense
                          outlined
                          hide-details
                          :disabled="submitting || !selection.packageName"
                          :no-data-text="selection.packageName ? 'No APKs found for this app' : 'Select an app first'"
                          @change="syncDefaultSelection"
                        >
                          <template v-slot:item="{ item, on, attrs }">
                            <v-list-item
                              v-bind="attrs"
                              :class="{ 'default-version-option': isDefaultApkOption(item, selection.packageName) }"
                              v-on="on"
                            >
                              <v-list-item-content>
                                <v-list-item-title>
                                  {{ item.displayLabel }}
                                  <v-chip
                                    v-if="isDefaultApkOption(item, selection.packageName)"
                                    x-small
                                    color="success"
                                    text-color="white"
                                    class="ml-2"
                                  >
                                    Default
                                  </v-chip>
                                </v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-autocomplete>
                      </v-col>

                      <v-col
                        v-if="selectionIndex > 0"
                        cols="12"
                        md="1"
                        class="device-version-update-selection-remove"
                      >
                        <v-btn
                          icon
                          small
                          color="error"
                          :disabled="submitting"
                          title="Remove additional app"
                          @click="removeSelection(row, selection)"
                        >
                          <v-icon small>mdi-minus-circle-outline</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>

                <v-col
                  cols="12"
                  md="1"
                  class="device-version-update-actions"
                >
                  <v-btn
                    icon
                    color="primary"
                    :disabled="submitting || !appChoices.length"
                    title="Add another app"
                    @click="addSelection(row)"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    color="error"
                    :disabled="submitting"
                    title="Remove device"
                    @click="removeRow(row)"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <div
                v-if="row.error"
                class="caption error--text mt-2"
              >
                {{ row.error }}
              </div>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          :disabled="submitting"
          @click="dialog = false"
        >
          Cancel
        </v-btn>
        <app-btn
          :loading="submitting"
          :disabled="disableSubmit"
          @click="submitUpdates"
        >
          {{ target.mode === 'group' ? 'Send To Group' : 'Bulk Update' }}
        </app-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import SettingsDataService from '@/services/SettingsDataService'
  import CommandTargetSelector from './CommandTargetSelector.vue'
  import groupCommandTarget from '@/mixins/groupCommandTarget'

  const toastOptions = {
    position: 'topRight',
  }
  const ANDROID_PACKAGE_FILE_PATTERN = /\.(apk|apkm)(?=$|[?#\s])/i

  export default {
    name: 'DeviceVersionUpdateDialog',
    components: {
      CommandTargetSelector,
    },
    mixins: [groupCommandTarget],

    props: {
      value: {
        type: Boolean,
        default: false,
      },
      devices: {
        type: Array,
        default: () => [],
      },
      companyId: {
        type: Number,
        default: 0,
      },
      companyName: {
        type: String,
        default: '',
      },
      companyPath: {
        type: Array,
        default: () => [],
      },
    },

    data: () => ({
      target: { mode: 'devices', groupId: null, error: '' },
      dialog: false,
      catalogLoading: false,
      submitting: false,
      catalogError: '',
      companyApps: [],
      apkOptions: [],
      appChoices: [],
      updateRows: [],
      defaultPackageName: '',
      defaultSelectedApkId: null,
      appliedDefaultPackageName: '',
      appliedDefaultSelectedApkId: null,
      loadedCompanyId: 0,
    }),

    computed: {
      hasMixedCompanies () {
        const companyNames = this.updateRows
          .map(row => String(row.companyName || '').trim())
          .filter(Boolean)

        return new Set(companyNames).size > 1
      },
      disableSubmit () {
        if (this.target.mode === 'group') {
          return (
            this.catalogLoading ||
            this.submitting ||
            !this.target.groupId ||
            !this.defaultPackageName ||
            !this.defaultSelectedApkId
          )
        }

        return (
          this.catalogLoading ||
          this.submitting ||
          !this.updateRows.length ||
          !this.updateRows.every(row =>
            !!row.macAddress &&
            Array.isArray(row.selections) &&
            row.selections.length > 0 &&
            row.selections.every(selection => !!selection.selectedApkId),
          )
        )
      },
      isAppliedDefaultActive () {
        if (
          !this.defaultPackageName ||
          !this.defaultSelectedApkId ||
          !this.appliedDefaultPackageName ||
          !this.appliedDefaultSelectedApkId ||
          !this.updateRows.length
        ) {
          return false
        }

        return (
          this.defaultPackageName === this.appliedDefaultPackageName &&
          this.defaultSelectedApkId === this.appliedDefaultSelectedApkId &&
          this.updateRows.every((row) => {
            const selection = this.getPrimarySelection(row)
            return (
              selection?.packageName === this.defaultPackageName &&
              selection?.selectedApkId === this.defaultSelectedApkId
            )
          })
        )
      },
    },

    watch: {
      value: {
        immediate: true,
        handler (val) {
          this.dialog = val
        },
      },
      dialog: {
        immediate: true,
        handler (val) {
          this.$emit('input', val)
          if (val) {
            this.prepareDialog()
          } else {
            this.updateRows = []
            this.defaultPackageName = ''
            this.defaultSelectedApkId = null
            this.appliedDefaultPackageName = ''
            this.appliedDefaultSelectedApkId = null
          }
        },
      },
      devices: {
        deep: true,
        handler () {
          if (this.dialog && !this.catalogLoading) {
            this.buildRows()
          }
        },
      },
      companyId (val, oldVal) {
        if (val !== oldVal) {
          this.loadedCompanyId = 0
          this.companyApps = []
          this.apkOptions = []
          this.appChoices = []
          if (this.dialog) {
            this.prepareDialog({ force: true })
          }
        }
      },
    },

    methods: {
      async prepareDialog ({ force = false } = {}) {
        this.target = {
          mode: this.devices.length ? 'devices' : 'group',
          groupId: null,
          error: '',
        }
        this.loadDeviceGroups()
        this.defaultPackageName = ''
        this.defaultSelectedApkId = null
        this.appliedDefaultPackageName = ''
        this.appliedDefaultSelectedApkId = null
        await this.loadCatalog({ force })
        this.buildRows()
      },
      async submitGroupUpdate () {
        if (!this.target.groupId) {
          this.target.error = 'Select a device group'
          this.$toast.error('Select a device group.', 'Error', toastOptions)
          return
        }

        const apk = this.apkOptions.find(option => option.id === this.defaultSelectedApkId)
        if (!apk) {
          this.$toast.error('Select an APK version to send.', 'Error', toastOptions)
          return
        }

        this.submitting = true
        try {
          const sent = await this.sendGroupCommand(this.target.groupId, 'RequiredVersionUpdated', {
            packageName: this.defaultPackageName,
            requiredVersion: apk.name,
            deviceConfigFilePathId: apk.deviceConfigFilePathId || 0,
          })
          if (sent) {
            this.dialog = false
          }
        } finally {
          this.submitting = false
        }
      },
      normalizePackageValue (value) {
        return String(value || '')
          .trim()
          .toLowerCase()
      },
      normalizeSearchToken (value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '')
      },
      buildAppMatchTokens (packageName, appChoice) {
        const normalizedPackage = this.normalizePackageValue(packageName)
        const packageSegments = normalizedPackage
          .split('.')
          .map(segment => segment.trim())
          .filter(Boolean)

        const ignoredSegments = new Set([
          'com',
          'net',
          'org',
          'io',
          'app',
          'android',
          'au',
          'uk',
          'us',
        ])

        const rawTokens = [
          normalizedPackage,
          packageSegments.slice(-2).join('.'),
          packageSegments[packageSegments.length - 1] || '',
          appChoice?.title || '',
          appChoice?.label || '',
        ]
          .concat(
            packageSegments.filter(segment =>
              segment.length >= 4 && !ignoredSegments.has(segment),
            ),
          )

        return [...new Set(
          rawTokens
            .map(token => String(token || '').trim().toLowerCase())
            .filter(token => token.length >= 4),
        )]
      },
      isApkRelatedToPackage (option, packageName, appChoice) {
        const normalizedPackage = this.normalizePackageValue(packageName)

        if (!normalizedPackage) {
          return false
        }

        // When the APK carries its own package id, require an exact match so that
        // apps sharing a namespace (e.g. com.example.mytaxy vs com.example.mydevice)
        // are not grouped together by fuzzy token matching.
        const optionPackage = this.normalizePackageValue(option?.packageName)
        if (optionPackage) {
          return optionPackage === normalizedPackage
        }

        const apkSearchSource = [
          option?.packageName,
          option?.name,
          option?.filePathLabel,
          option?.downloadUrl,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        if (apkSearchSource.includes(normalizedPackage)) {
          return true
        }

        const normalizedSearchSource = this.normalizeSearchToken(apkSearchSource)
        const matchTokens = this.buildAppMatchTokens(packageName, appChoice)

        return matchTokens.some((token) => {
          const normalizedToken = this.normalizeSearchToken(token)
          return normalizedToken.length >= 4 && normalizedSearchSource.includes(normalizedToken)
        })
      },
      normalizeConfigSearchLabel (value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .replace(/[_-]+/g, ' ')
          .replace(/\s+/g, ' ')
      },
      normalizeConfigSearchPath (value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .replace(/\\/g, '/')
          .replace(/^\/+|\/+$/g, '')
          .replace(/\/+/g, '/')
      },
      scoreDownloadsConfig (config) {
        const normalizedName = this.normalizeConfigSearchLabel(config?.name)
        const normalizedPath = this.normalizeConfigSearchPath(config?.devicePath)
        const lastPathSegment = normalizedPath.split('/').filter(Boolean).pop() || ''

        let score = -1

        if (['download', 'downloads'].includes(lastPathSegment)) {
          score = Math.max(score, 100)
        }

        if (
          normalizedPath === 'download' ||
          normalizedPath === 'downloads' ||
          normalizedPath.endsWith('/download') ||
          normalizedPath.endsWith('/downloads')
        ) {
          score = Math.max(score, 95)
        }

        if (
          ['download', 'downloads', 'script download', 'script downloads', 'scripts download', 'scripts downloads']
            .includes(normalizedName)
        ) {
          score = Math.max(score, 90)
        }

        if (/\bdownloads?\b/.test(normalizedName) && /\bscripts?\b/.test(normalizedName)) {
          score = Math.max(score, 85)
        }

        if (/\bdownloads?\b/.test(normalizedName)) {
          score = Math.max(score, 75)
        }

        if (/\bdownloads?\b/.test(normalizedPath)) {
          score = Math.max(score, 70)
        }

        return score
      },
      getDownloadsSearchCompanyIds () {
        return this.companyId ? [this.companyId] : []
      },
      async getDeviceConfigsByCompany (companyId) {
        const response = await this.$http.get('DeviceConfig/GetDeviceConfigsByCompany', {
          params: {
            CompanyId: companyId,
          },
        })

        return Array.isArray(response.data) ? response.data : []
      },
      async findDownloadsConfigs () {
        const searchCompanyIds = this.getDownloadsSearchCompanyIds()
        const configResults = await Promise.all(
          searchCompanyIds.map(async (companyId) => ({
            companyId,
            configs: await this.getDeviceConfigsByCompany(companyId),
          })),
        )

        const matches = []

        configResults.forEach(({ companyId, configs }, companyIndex) => {
          configs.forEach((config) => {
            const score = this.scoreDownloadsConfig(config)
            if (score < 0) {
              return
            }

            matches.push({
              companyId,
              companyIndex,
              config,
              score,
            })
          })
        })

        matches.sort((left, right) => {
          if (right.score !== left.score) {
            return right.score - left.score
          }

          if (left.companyIndex !== right.companyIndex) {
            return left.companyIndex - right.companyIndex
          }

          return String(left.config?.name || '').localeCompare(String(right.config?.name || ''))
        })

        return matches
      },
      getNestedStringValue (candidate) {
        if (typeof candidate === 'string') {
          return candidate.trim()
        }

        if (candidate && typeof candidate === 'object') {
          const nestedCandidates = [
            candidate.path,
            candidate.filePath,
            candidate.fileName,
            candidate.name,
            candidate.url,
          ]

          return nestedCandidates.find(value => typeof value === 'string' && value.trim())
        }

        return ''
      },
      buildDownloadUrlFromPath (filePath) {
        const resourceBaseUrl = this.$http.defaults.baseURL.replace('api/', 'Resources/')
        const normalizedPath = String(filePath || '')
          .replace(/\\/g, '/')
          .replace(/^\/+/, '')
          .replace(/^resources\/+/i, '')

        return new URL(normalizedPath, resourceBaseUrl).toString()
      },
      resolveUploadFilePath (file, fallbackName) {
        const candidates = [
          file?.filePath,
          file?.path,
          file?.downloadUrl,
          file?.url,
          file?.name,
        ]

        for (const candidate of candidates) {
          const stringValue = this.getNestedStringValue(candidate)
          if (!stringValue) {
            continue
          }

          if (/^https?:\/\//i.test(stringValue) || /^download\//i.test(stringValue)) {
            return stringValue
          }

          if (ANDROID_PACKAGE_FILE_PATTERN.test(stringValue)) {
            return `Download/${stringValue.split(/[\\/]/).pop()}`
          }
        }

        return `Download/${fallbackName}`
      },
      normalizeApkOption (file) {
        const fileName = this.getNestedStringValue(file) || 'Unnamed APK'
        const resolvedPathOrUrl = this.resolveUploadFilePath(file, fileName)
        const apkSearchSource = [
          fileName,
          resolvedPathOrUrl,
          file?.filePath,
          file?.path,
        ]
          .filter(Boolean)
          .join(' ')

        if (!ANDROID_PACKAGE_FILE_PATTERN.test(apkSearchSource)) {
          return null
        }

        const downloadUrl = /^https?:\/\//i.test(resolvedPathOrUrl)
          ? resolvedPathOrUrl
          : this.buildDownloadUrlFromPath(resolvedPathOrUrl)

        const versionLabel = file?.versionName || file?.version || ''

        return {
          id: file?.id || file?.deviceConfigFilePathId || downloadUrl,
          filePathId: file?.filePathId || file?.id || 0,
          name: fileName,
          packageName: file?.packageName || file?.package || '',
          versionName: versionLabel,
          versionCode: file?.versionCode || 0,
          uploadedAt: file?.uploadedAt || '',
          isLatestForPackage: !!file?.isLatestForPackage,
          filePathLabel: /^https?:\/\//i.test(resolvedPathOrUrl)
            ? resolvedPathOrUrl
            : resolvedPathOrUrl.replace(/^\/+/, ''),
          downloadUrl,
          deviceConfigId: file?.deviceConfigId || file?._downloadsConfigId || 0,
          deviceConfigFilePathId: file?.deviceConfigFilePathId || file?.id || 0,
          companyId: file?.companyId || file?._downloadsCompanyId || this.companyId,
          displayLabel: versionLabel
            ? `${versionLabel} - ${fileName}`
            : fileName,
        }
      },
      sortApkOptions (items) {
        return [...items].sort((left, right) => {
          if (Number(right.isLatestForPackage) !== Number(left.isLatestForPackage)) {
            return Number(right.isLatestForPackage) - Number(left.isLatestForPackage)
          }

          if (Number(right.versionCode || 0) !== Number(left.versionCode || 0)) {
            return Number(right.versionCode || 0) - Number(left.versionCode || 0)
          }

          if (String(right.uploadedAt || '') !== String(left.uploadedAt || '')) {
            return String(right.uploadedAt || '').localeCompare(String(left.uploadedAt || ''))
          }

          return String(left.name || '').localeCompare(String(right.name || ''))
        })
      },
      async loadCompanyApps () {
        if (!this.companyId) {
          return []
        }

        const items = []
        let page = 1
        const pageSize = 200
        let totalCount = 0

        while (page <= 10) {
          const response = await this.$http.post(`/CompanyKioskApp/paginated/${this.companyId}`, {
            pageNumber: page,
            pageSize,
            search: '',
          })

          const result = response?.data?.data
          const pageItems = Array.isArray(result?.items) ? result.items : []
          totalCount = Number(result?.totalCount || pageItems.length)
          items.push(...pageItems)

          if (!pageItems.length || items.length >= totalCount) {
            break
          }

          page += 1
        }

        return items
      },
      buildAppChoices () {
        const choiceMap = new Map()

        this.companyApps.forEach((app) => {
          const packageName = String(app?.packageName || '').trim()
          if (!packageName) {
            return
          }

          const hasMatchingApk = this.apkOptions.some(option =>
            this.isApkRelatedToPackage(option, packageName, app),
          )

          if (!hasMatchingApk) {
            return
          }

          if (!choiceMap.has(packageName)) {
            const title = String(app?.title || app?.label || packageName).trim()
            choiceMap.set(packageName, {
              packageName,
              title: app?.title || '',
              label: app?.label || '',
              displayLabel: title === packageName
                ? packageName
                : `${title} (${packageName})`,
            })
          }
        })

        this.apkOptions.forEach((option) => {
          const packageName = String(option?.packageName || '').trim()
          if (!packageName || choiceMap.has(packageName)) {
            return
          }

          choiceMap.set(packageName, {
            packageName,
            title: packageName,
            label: packageName,
            displayLabel: packageName,
          })
        })

        this.appChoices = [...choiceMap.values()].sort((left, right) =>
          left.displayLabel.localeCompare(right.displayLabel),
        )
      },
      getAppChoice (packageName) {
        return this.appChoices.find(choice => choice.packageName === packageName) || null
      },
      getDefaultFilePathIdForPackage (packageName) {
        const normalizedPackage = this.normalizePackageValue(packageName)
        if (!normalizedPackage) {
          return 0
        }

        const app = this.companyApps.find(candidate =>
          this.normalizePackageValue(candidate?.packageName) === normalizedPackage,
        )

        return app?.defaultFilePathId || 0
      },
      isDefaultApkOption (option, packageName) {
        const defaultFilePathId = this.getDefaultFilePathIdForPackage(packageName)
        return !!defaultFilePathId && option?.filePathId === defaultFilePathId
      },
      createSelection ({ packageName = '', selectedApkId = null } = {}) {
        return {
          id: `selection-${Math.random().toString(36).slice(2, 10)}`,
          packageName,
          selectedApkId,
        }
      },
      normalizeSelections (existingRow, matchedApk, defaultPackageName) {
        const legacySelection = existingRow
          ? this.createSelection({
            packageName: existingRow.packageName,
            selectedApkId: existingRow.selectedApkId,
          })
          : null

        const rawSelections = Array.isArray(existingRow?.selections) && existingRow.selections.length
          ? existingRow.selections
          : (legacySelection ? [legacySelection] : [])

        const normalizedSelections = rawSelections.map((selection, index) => {
          const initialPackageName = String(selection?.packageName || '').trim() ||
            (index === 0 ? matchedApk?.packageName || defaultPackageName : '')
          const apkOptions = this.getApkOptionsForPackage(initialPackageName)
          const selectedApkId = apkOptions.some(option => option.id === selection?.selectedApkId)
            ? selection.selectedApkId
            : (index === 0 && matchedApk && apkOptions.some(option => option.id === matchedApk.id)
              ? matchedApk.id
              : (apkOptions[0]?.id || null))

          return this.createSelection({
            packageName: initialPackageName,
            selectedApkId,
          })
        })

        if (normalizedSelections.length) {
          return normalizedSelections
        }

        const apkOptions = this.getApkOptionsForPackage(matchedApk?.packageName || defaultPackageName)
        return [this.createSelection({
          packageName: matchedApk?.packageName || defaultPackageName,
          selectedApkId: matchedApk?.id || apkOptions[0]?.id || null,
        })]
      },
      getApkOptionsForPackage (packageName) {
        const appChoice = this.getAppChoice(packageName)
        const options = this.sortApkOptions(
          this.apkOptions.filter(option =>
            this.isApkRelatedToPackage(option, packageName, appChoice),
          ),
        )

        const defaultFilePathId = this.getDefaultFilePathIdForPackage(packageName)
        if (!defaultFilePathId) {
          return options
        }

        return [...options].sort((left, right) => {
          const leftIsDefault = left.filePathId === defaultFilePathId ? 1 : 0
          const rightIsDefault = right.filePathId === defaultFilePathId ? 1 : 0
          return rightIsDefault - leftIsDefault
        })
      },
      findApkByRequiredVersion (requiredVersion) {
        const normalizedRequiredVersion = String(requiredVersion || '')
          .trim()
          .toLowerCase()

        if (!normalizedRequiredVersion) {
          return null
        }

        return this.apkOptions.find((option) => {
          const candidates = [
            option?.name,
            option?.filePathLabel,
            option?.downloadUrl,
          ]

          return candidates.some(candidate =>
            String(candidate || '').trim().toLowerCase().includes(normalizedRequiredVersion),
          )
        }) || null
      },
      buildRows () {
        const existingRows = this.updateRows.reduce((result, row) => {
          result[this.getDeviceKey(row)] = row
          return result
        }, {})

        this.updateRows = (this.devices || []).map((device) => {
          const existingRow = existingRows[this.getDeviceKey(device)] || null
          const matchedApk = this.findApkByRequiredVersion(device?.requiredVersion)
          const defaultPackageName = this.appChoices.length === 1
            ? this.appChoices[0].packageName
            : ''

          return {
            id: device?.id || device?.macAddress || Math.random().toString(36),
            deviceName: this.getDeviceDisplayName(device),
            deviceIdentifier: device?.macAddress || '',
            companyName: this.getDeviceCompanyName(device),
            macAddress: device?.macAddress || '',
            currentRequiredVersion: device?.requiredVersion || '',
            currentAppVersion: device?.appVersion || '',
            selections: this.normalizeSelections(existingRow, matchedApk, defaultPackageName),
            error: existingRow?.error || '',
          }
        })

        this.syncDefaultSelection()
      },
      getDeviceDisplayName (device) {
        const preferredCandidates = [
          device?.deviceModel,
          device?.name,
          device?.deviceName,
          device?.model,
        ]

        const preferredName = preferredCandidates.find(value => {
          const text = String(value || '').trim()
          return text && text.toLowerCase() !== String(device?.macAddress || '').trim().toLowerCase()
        })

        return preferredName || device?.macAddress || 'Unnamed device'
      },
      getCompanyName (company) {
        const value = String(company || '').trim()
        if (!value) {
          return ''
        }

        const names = value.split('->').map(item => String(item || '').trim()).filter(Boolean)
        return names.length ? names[names.length - 1] : value
      },
      getDeviceCompanyName (device) {
        return this.getCompanyName(
          device?.company ||
          device?.companyName ||
          device?.companyPath ||
          this.companyName,
        )
      },
      getCompanyBadgeStyle (companyName) {
        if (!this.hasMixedCompanies) {
          return {
            backgroundColor: '#eceff1',
            color: '#455a64',
          }
        }

        const hue = this.getCompanyHue(companyName)

        return {
          backgroundColor: `hsl(${hue}, 55%, 42%)`,
          color: '#fff',
        }
      },
      getCompanyHue (value) {
        const text = String(value || '')
        let hash = 0

        for (let index = 0; index < text.length; index += 1) {
          hash = ((hash << 5) - hash) + text.charCodeAt(index)
          hash |= 0
        }

        return Math.abs(hash) % 360
      },
      getDeviceKey (device) {
        return String(device?.macAddress || device?.id || '')
      },
      getPrimarySelection (row) {
        return Array.isArray(row?.selections) && row.selections.length
          ? row.selections[0]
          : null
      },
      removeRow (row) {
        const remainingDevices = (this.devices || []).filter(device =>
          this.getDeviceKey(device) !== this.getDeviceKey(row),
        )

        this.updateRows = this.updateRows.filter(item =>
          this.getDeviceKey(item) !== this.getDeviceKey(row),
        )

        this.$emit('devices-change', remainingDevices)

        this.syncDefaultSelection()

        if (!remainingDevices.length) {
          this.dialog = false
        }
      },
      onDefaultPackageChanged () {
        const apkOptions = this.getApkOptionsForPackage(this.defaultPackageName)
        const hasCurrentSelection = apkOptions.some(option => option.id === this.defaultSelectedApkId)
        this.defaultSelectedApkId = hasCurrentSelection ? this.defaultSelectedApkId : (apkOptions[0]?.id || null)
      },
      applyDefaultSelection () {
        if (!this.defaultPackageName || !this.defaultSelectedApkId) {
          return
        }

        this.updateRows.forEach((row) => {
          if (!Array.isArray(row.selections) || !row.selections.length) {
            row.selections = [this.createSelection()]
          }

          row.selections[0].packageName = this.defaultPackageName
          row.selections[0].selectedApkId = this.defaultSelectedApkId
          row.error = ''
        })

        this.appliedDefaultPackageName = this.defaultPackageName
        this.appliedDefaultSelectedApkId = this.defaultSelectedApkId
      },
      clearAppliedDefault () {
        if (!this.isAppliedDefaultActive) {
          return
        }

        this.updateRows.forEach((row) => {
          const selection = this.getPrimarySelection(row)
          if (
            selection?.packageName === this.appliedDefaultPackageName &&
            selection?.selectedApkId === this.appliedDefaultSelectedApkId
          ) {
            selection.packageName = ''
            selection.selectedApkId = null
            row.error = ''
          }
        })

        this.defaultPackageName = ''
        this.defaultSelectedApkId = null
        this.appliedDefaultPackageName = ''
        this.appliedDefaultSelectedApkId = null
      },
      addSelection (row) {
        if (!Array.isArray(row.selections)) {
          row.selections = []
        }

        row.selections.push(this.createSelection())
        row.error = ''
        this.syncDefaultSelection()
      },
      removeSelection (row, selectionToRemove) {
        if (!Array.isArray(row.selections) || row.selections.length <= 1) {
          return
        }

        row.selections = row.selections.filter(selection => selection.id !== selectionToRemove.id)
        row.error = ''
        this.syncDefaultSelection()
      },
      onSelectionPackageChanged (row, selection) {
        row.error = ''
        const apkOptions = this.getApkOptionsForPackage(selection.packageName)
        const hasCurrentSelection = apkOptions.some(option => option.id === selection.selectedApkId)
        selection.selectedApkId = hasCurrentSelection ? selection.selectedApkId : (apkOptions[0]?.id || null)
        this.syncDefaultSelection()
      },
      syncDefaultSelection () {
        const validPackageNames = this.appChoices.map(choice => choice.packageName)
        const rowPackageNames = [...new Set(
          this.updateRows
            .map((row) => {
              const selection = this.getPrimarySelection(row)
              return String(selection?.packageName || '').trim()
            })
            .filter(Boolean),
        )]

        if (
          this.defaultPackageName &&
          !validPackageNames.includes(this.defaultPackageName)
        ) {
          this.defaultPackageName = ''
        }

        if (
          this.appliedDefaultPackageName &&
          !validPackageNames.includes(this.appliedDefaultPackageName)
        ) {
          this.appliedDefaultPackageName = ''
          this.appliedDefaultSelectedApkId = null
        }

        if (!this.defaultPackageName && rowPackageNames.length === 1) {
          this.defaultPackageName = rowPackageNames[0]
        }

        const defaultApkOptions = this.getApkOptionsForPackage(this.defaultPackageName)
        const validDefaultApkIds = defaultApkOptions.map(option => option.id)
        const rowSelectedApkIds = [...new Set(
          this.updateRows
            .filter((row) => {
              const selection = this.getPrimarySelection(row)
              return selection?.packageName === this.defaultPackageName
            })
            .map((row) => this.getPrimarySelection(row)?.selectedApkId)
            .filter(Boolean),
        )]

        if (
          this.defaultSelectedApkId &&
          !validDefaultApkIds.includes(this.defaultSelectedApkId)
        ) {
          this.defaultSelectedApkId = null
        }

        if (
          this.appliedDefaultPackageName === this.defaultPackageName &&
          this.appliedDefaultSelectedApkId &&
          !validDefaultApkIds.includes(this.appliedDefaultSelectedApkId)
        ) {
          this.appliedDefaultPackageName = ''
          this.appliedDefaultSelectedApkId = null
        }

        if (!this.defaultPackageName) {
          this.defaultSelectedApkId = null
          return
        }

        if (!this.defaultSelectedApkId && rowSelectedApkIds.length === 1) {
          this.defaultSelectedApkId = rowSelectedApkIds[0]
        }
      },
      async loadCatalog ({ force = false } = {}) {
        if (!force && this.loadedCompanyId === this.companyId && this.apkOptions.length) {
          return
        }

        if (!this.companyId) {
          this.catalogError = 'Select a company before updating app versions.'
          this.companyApps = []
          this.apkOptions = []
          this.appChoices = []
          return
        }

        this.catalogLoading = true
        this.catalogError = ''

        try {
          const [companyApps, downloadsConfigs] = await Promise.all([
            this.loadCompanyApps().catch(() => []),
            this.findDownloadsConfigs(),
          ])

          if (!downloadsConfigs.length) {
            this.companyApps = companyApps
            this.apkOptions = []
            this.appChoices = []
            this.catalogError = 'Downloads script config was not found for this company.'
            this.loadedCompanyId = this.companyId
            return
          }

          const files = (
            await Promise.all(
              downloadsConfigs.map(async ({ companyId, config }) => {
                const response = await this.$http.get('DeviceConfig/GetFilePathsByDeviceConfigs', {
                  params: {
                    DeviceConfigId: config.id,
                  },
                })

                const configFiles = Array.isArray(response.data) ? response.data : []
                return configFiles.map(file => ({
                  ...file,
                  _downloadsConfigId: config.id,
                  _downloadsCompanyId: companyId || this.companyId,
                }))
              }),
            )
          ).flat()

          const seenOptions = new Set()
          this.companyApps = companyApps
          this.apkOptions = this.sortApkOptions(
            files
              .map(file => this.normalizeApkOption(file))
              .filter((option) => {
                if (!option || seenOptions.has(option.id)) {
                  return false
                }

                seenOptions.add(option.id)
                return true
              }),
          )
          this.buildAppChoices()
          this.loadedCompanyId = this.companyId

          if (!this.apkOptions.length) {
            this.catalogError = 'No APK files were found in Scripts Downloads for this company.'
          }
        } catch (error) {
          this.companyApps = []
          this.apkOptions = []
          this.appChoices = []
          this.catalogError =
            error?.response?.data?.message ||
            error?.message ||
            'We could not load APKs for version updates right now.'
        } finally {
          this.catalogLoading = false
        }
      },
      setRowErrors (rows, message) {
        rows.forEach((row) => {
          row.error = message
        })
      },
      async submitUpdates () {
        if (this.target.mode === 'group') {
          await this.submitGroupUpdate()
          return
        }

        this.updateRows.forEach((row) => {
          row.error = ''
        })

        const invalidRows = this.updateRows.filter(row =>
          !row.macAddress ||
          !Array.isArray(row.selections) ||
          !row.selections.length ||
          row.selections.some(selection => !selection.selectedApkId),
        )
        if (invalidRows.length) {
          this.setRowErrors(invalidRows, 'Select an APK version for this device.')
          this.$toast.error('Complete the app version selection for every device.', 'Error', toastOptions)
          return
        }

        const groupedUpdates = new Map()
        const updateEntries = []

        this.updateRows.forEach((row) => {
          row.selections.forEach((selection) => {
            const apk = this.apkOptions.find(option => option.id === selection.selectedApkId)
            if (!apk) {
              return
            }

            const entry = {
              row,
              selection,
              apk,
            }
            updateEntries.push(entry)

            const groupKey = apk.deviceConfigFilePathId
              ? `bulk:${apk.deviceConfigFilePathId}`
              : `single:${apk.name}`

            if (!groupedUpdates.has(groupKey)) {
              groupedUpdates.set(groupKey, {
                apk,
                entries: [],
              })
            }

            groupedUpdates.get(groupKey).entries.push(entry)
          })
        })

        this.submitting = true
        const successfulUpdates = []

        try {
          const results = await Promise.allSettled(
            [...groupedUpdates.values()].map(async (group) => {
              const { apk, entries } = group

              if (apk.deviceConfigFilePathId) {
                const response = await SettingsDataService.updateRequiredVersionBulk({
                  macAddresses: [...new Set(entries.map(entry => entry.row.macAddress))],
                  deviceConfigFilePathId: apk.deviceConfigFilePathId,
                })

                const ok = response?.data?.success !== false && response?.data?.isSuccess !== false
                if (!ok) {
                  throw new Error(response?.data?.message || 'Bulk update failed.')
                }

                return {
                  entries,
                  apk,
                }
              }

              const singleResults = await Promise.allSettled(
                entries.map(({ row }) =>
                  SettingsDataService.updateRequiredVersion({
                    macAddress: row.macAddress,
                    requiredVersion: apk.name,
                  }),
                ),
              )

              const failedResult = singleResults.find(result =>
                result.status !== 'fulfilled' ||
                (result.value?.data?.success === false || result.value?.data?.isSuccess === false),
              )

              if (failedResult) {
                const errorMessage = failedResult.reason?.response?.data?.message ||
                  failedResult.reason?.message ||
                  failedResult.value?.data?.message ||
                  'Version update failed.'
                throw new Error(errorMessage)
              }

              return {
                entries,
                apk,
              }
            }),
          )

          results.forEach((result, index) => {
            const group = [...groupedUpdates.values()][index]
            if (result.status === 'fulfilled') {
              result.value.entries.forEach(({ row }) => {
                successfulUpdates.push({
                  macAddress: row.macAddress,
                  requiredVersion: result.value.apk.name,
                })
              })
              return
            }

            this.setRowErrors(
              [...new Set(group.entries.map(entry => entry.row))],
              result.reason?.response?.data?.message || result.reason?.message || 'Version update failed.',
            )
          })

          if (successfulUpdates.length) {
            this.$emit('updated', successfulUpdates)
          }

          if (successfulUpdates.length === updateEntries.length) {
            this.$toast.success(
              `Queued ${successfulUpdates.length} app update${successfulUpdates.length === 1 ? '' : 's'}.`,
              'Success',
              toastOptions,
            )
            this.dialog = false
            return
          }

          if (successfulUpdates.length > 0) {
            this.$toast.error(
              `Queued ${successfulUpdates.length} of ${updateEntries.length} app updates. Review the rows with errors and retry.`,
              'Error',
              toastOptions,
            )
            return
          }

          this.$toast.error('Required version update failed for the selected app updates.', 'Error', toastOptions)
        } finally {
          this.submitting = false
        }
      },
    },
  }
</script>

<style scoped>
  .device-version-update-subtitle {
    color: rgba(0, 0, 0, 0.6);
  }

  .device-version-update-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .device-version-update-loading {
    display: flex;
    justify-content: center;
  }

  .device-version-update-defaults-action {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .device-version-update-company {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.4;
    letter-spacing: 0.01em;
  }

  .device-version-update-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .device-version-update-selection-remove {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .default-version-option {
    background: #e8f5e9;
  }

  @media (max-width: 600px) {
    .device-version-update-toolbar {
      flex-direction: column;
      align-items: stretch;
    }

    .device-version-update-defaults-action {
      justify-content: stretch;
    }
  }
</style>
